import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, FileSearchIcon, ServerIcon, ArrowRightIcon, BoxIcon, LockIcon, UsersIcon, HelpCircleIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function HomePage() {
  const navigate = useNavigate();
  const valueProps = [{
    title: 'Enforceable Control',
    description: 'Deterministic policy enforcement that sits between your AI agents and your data. No open-ended access.',
    icon: <ShieldCheckIcon className="h-8 w-8 text-[#4db8a4]" data-id="element-208" />,
    link: '/product'
  }, {
    title: 'Full Auditability',
    description: 'Every AI action tracked: who requested it, who executed it, what was accessed, and why it was allowed.',
    icon: <FileSearchIcon className="h-8 w-8 text-[#4db8a4]" data-id="element-209" />,
    link: '/security'
  }, {
    title: 'Deploy Anywhere',
    description: 'Cloud, on-premise, or hybrid. Meet your compliance requirements with flexible deployment options.',
    icon: <ServerIcon className="h-8 w-8 text-[#4db8a4]" data-id="element-210" />,
    link: '/product'
  }];
  const quickLinks = [{
    title: 'Product',
    description: 'Explore MCPGuard capabilities',
    icon: <BoxIcon className="h-5 w-5 text-[#1e3a5f]" data-id="element-211" />,
    link: '/product'
  }, {
    title: 'Security',
    description: 'Deep dive into our audit trails',
    icon: <LockIcon className="h-5 w-5 text-[#1e3a5f]" data-id="element-212" />,
    link: '/security'
  }, {
    title: 'Use Cases',
    description: 'See how teams use MCPGuard',
    icon: <UsersIcon className="h-5 w-5 text-[#1e3a5f]" data-id="element-213" />,
    link: '/use-cases'
  }, {
    title: 'FAQ',
    description: 'Answers to common questions',
    icon: <HelpCircleIcon className="h-5 w-5 text-[#1e3a5f]" data-id="element-214" />,
    link: '/faq'
  }];
  return <div className="w-full animate-in fade-in duration-500" data-id="element-215">
      {/* ─── SECTION 1: Hero ─── */}
      <section className="max-w-6xl mx-auto px-4 py-24 md:py-36 text-center" data-id="element-216">
        <h1 className="text-5xl md:text-6xl font-bold text-[#1e3a5f] mb-6 tracking-tight leading-tight" data-id="element-217">
          The Trust Layer for Agentic AI
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-218">
          ProvenanceOne MCPGuard gives enterprises deterministic control, full
          auditability, and regulator-defensible security for autonomous AI
          agents.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-219">
          <Button size="lg" onClick={() => navigate('/product')} className="w-full sm:w-auto min-w-[200px]" data-id="element-220">
            Explore the Product
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate('/early-access')} className="w-full sm:w-auto min-w-[200px]" data-id="element-221">
            Request Access
          </Button>
        </div>
      </section>

      {/* ─── SECTION 2: Value Proposition Cards ─── */}
      <section className="max-w-5xl mx-auto px-4 pb-24" data-id="element-222">
        <div className="grid md:grid-cols-3 gap-6" data-id="element-223">
          {valueProps.map((prop, index) => <div key={index} onClick={() => navigate(prop.link)} className="bg-white/50 backdrop-blur-md border border-white/30 border-l-4 border-l-[#4db8a4] rounded-xl p-8 hover:shadow-md hover:border-[#4db8a4] hover:bg-white/70 transition-all cursor-pointer group" data-id="element-224">
              <div className="bg-[#4db8a4]/10 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-225">
                {prop.icon}
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-3" data-id="element-226">
                {prop.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-227">
                {prop.description}
              </p>
            </div>)}
        </div>
      </section>

      {/* ─── SECTION 3: Trust Banner ─── */}
      <section className="max-w-5xl mx-auto px-4 pb-24" data-id="element-228">
        <div className="bg-[#1e3a5f] rounded-2xl p-10 md:p-16 text-center text-white shadow-xl" data-id="element-229">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-230">
            Built for Highly Regulated Environments
          </h2>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-231">
            Financial services, healthcare, government, and any enterprise where
            AI compliance is non-negotiable.
          </p>
          <Button size="lg" className="bg-[#4db8a4] hover:bg-[#3a9e8c] text-white border-none" onClick={() => navigate('/use-cases')} data-id="element-232">
            See Use Cases
          </Button>
        </div>
      </section>

      {/* ─── SECTION 4: Quick Links Grid ─── */}
      <section className="max-w-5xl mx-auto px-4 pb-24" data-id="element-233">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-234">
          {quickLinks.map((link, index) => <div key={index} onClick={() => navigate(link.link)} className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:bg-white hover:border-[#4db8a4] hover:shadow-sm transition-all cursor-pointer group flex flex-col h-full" data-id="element-235">
              <div className="flex items-center gap-3 mb-3" data-id="element-236">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-100" data-id="element-237">
                  {link.icon}
                </div>
                <h3 className="text-lg font-bold text-[#1e3a5f] group-hover:text-[#4db8a4] transition-colors" data-id="element-238">
                  {link.title}
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-239">
                {link.description}
              </p>
              <div className="flex items-center text-sm font-medium text-[#4db8a4] mt-auto" data-id="element-240">
                Learn more{' '}
                <ArrowRightIcon className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-241" />
              </div>
            </div>)}
        </div>
      </section>

      {/* ─── SECTION 5: Final CTA ─── */}
      <section className="max-w-4xl mx-auto px-4 pb-32 text-center" data-id="element-242">
        <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8" data-id="element-243">
          Ready to get started?
        </h2>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-244">
          <Button size="lg" onClick={() => navigate('/early-access')} className="w-full sm:w-auto min-w-[200px]" data-id="element-245">
            Join the Waitlist
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate('/early-access')} className="w-full sm:w-auto min-w-[200px]" data-id="element-246">
            Talk to Us
          </Button>
        </div>
      </section>
    </div>;
}